import { ChangeDetectionStrategy, Component, computed, effect, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { GuestlinePmsSetup, PmsType } from '../../../../../types/pms';

@Component({
    selector: 'juyo-guestline-connect',
    styleUrl: './guestline.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule],
    template: `
        <h3 class="step-title">Connect to Guestline</h3>
        <p class="step-subtitle">Share your Site ID and Group ID below once your PO with Guestline is in place.</p>

        <div class="warning-box">
            <span class="warning-label">PO required:</span> Contact your Guestline account manager to arrange the
            interface agreement. Juyo cannot initiate this on your behalf. Guestline may apply charges, subject to your
            own agreement.
        </div>

        <div class="field-grid">
            <div class="field">
                <label class="field-label" for="guestline-site-id">SITE ID <span class="required">*</span></label>
                <input
                    id="guestline-site-id"
                    type="text"
                    class="field-input"
                    placeholder="e.g. ABC123"
                    [ngModel]="siteId()"
                    (ngModelChange)="siteId.set($event)"
                />
                <p class="field-hint">
                    Identifies your property — find this in your Guestline settings or ask your account manager.
                </p>
            </div>

            <div class="field">
                <label class="field-label" for="guestline-group-id">GROUP ID <span class="required">*</span></label>
                <input
                    id="guestline-group-id"
                    type="text"
                    class="field-input"
                    placeholder="e.g. ABC123"
                    [ngModel]="groupId()"
                    (ngModelChange)="groupId.set($event)"
                />
                <p class="field-hint">Identifies your hotel group. Can match Site ID if standalone.</p>
            </div>
        </div>

        <div class="next-box">
            <span class="material-symbols-outlined next-icon">info</span>
            <div class="next-content">
                <p class="next-title">What happens next</p>
                <p class="next-body">
                    Once we receive your Site ID + Group ID (and Guestline confirms your PO), Juyo configures the API
                    connection, validates data, and activates the integration. Setup typically takes a few days. We'll
                    reach out if we need additional information.
                </p>
            </div>
        </div>

        <p class="guide-link-text">
            Need more detail? Read the
            <a class="guide-link" [href]="guideUrl" target="_blank" rel="noopener noreferrer">
                full Guestline onboarding guide →
            </a>
        </p>
    `,
})
export class GuestlineConnectComponent {
    public valueChange = output<GuestlinePmsSetup | null>();

    protected readonly guideUrl =
        'https://intercom-help.eu/juyo-analytics/en/articles/620952-guestline-onboarding-what-to-expect';

    protected siteId = signal('');
    protected groupId = signal('');

    private setup = computed<GuestlinePmsSetup | null>(() => {
        const siteId = this.siteId().trim();
        const groupId = this.groupId().trim();

        if (!siteId || !groupId) {
            return null;
        }

        return { type: PmsType.Guestline, siteId, groupId };
    });

    constructor() {
        effect(() => this.valueChange.emit(this.setup()));
    }
}
